import { useCallback, useEffect, useState } from "react";
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormControl,
	FormLabel,
	FormErrorMessage,
	Input,
	Checkbox,
	Stack,
	Text,
	Alert,
	AlertIcon,
	Switch,
} from "@chakra-ui/react";

import { Button } from "newui/components/Button";
import type { RfxQuoteTable, SummaryTableConfig } from "../../Submission/types/quote.type";
import { validateSummaryTableValueColumns } from "../../api";

export interface CreateSummaryTableModalProps {
	availableQuoteTables: RfxQuoteTable[];
	editingTable?: SummaryTableConfig | null;
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (name: string, quoteTableIds: number[], valueColumnNames: string[], includeColumnTotals: boolean) => void;
	rfxId: number;
}

const CreateSummaryTableModal: React.FC<CreateSummaryTableModalProps> = ({
	availableQuoteTables,
	editingTable,
	isOpen,
	onClose,
	onSubmit,
	rfxId,
}) => {
	const [name, setName] = useState("");
	const [nameError, setNameError] = useState("");
	const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);
	const [selectedValueColumns, setSelectedValueColumns] = useState<string[]>([]);
	const [includeColumnTotals, setIncludeColumnTotals] = useState(false);
	const [validationErrors, setValidationErrors] = useState<string[]>([]);
	const [isValidating, setIsValidating] = useState(false);

	const isEditing = editingTable != null;

	// Find value columns that match across selected tables
	const { matchingValueColumns, hasNoMatches } = (() => {
		const selectedTables = availableQuoteTables.filter((qt) =>
			selectedTableIds.includes(qt.id)
		);
		if (selectedTables.length < 2) {
			// With fewer than 2 tables, show all value columns from selected tables
			const cols = new Set<string>();
			selectedTables.forEach((qt) => {
				qt.columns
					?.filter((c) => c.columnType === "response_value")
					.forEach((c) => cols.add(c.name));
			});
			return { matchingValueColumns: Array.from(cols), hasNoMatches: false };
		}

		// Count how many selected tables each value column name appears in
		const columnCounts: Record<string, number> = {};
		selectedTables.forEach((qt) => {
			const seen = new Set<string>();
			qt.columns
				?.filter((c) => c.columnType === "response_value")
				.forEach((c) => {
					if (!seen.has(c.name)) {
						seen.add(c.name);
						columnCounts[c.name] = (columnCounts[c.name] || 0) + 1;
					}
				});
		});

		const matching = Object.entries(columnCounts)
			.filter(([, count]) => count >= 2)
			.map(([colName]) => colName);

		return {
			matchingValueColumns: matching,
			hasNoMatches: matching.length === 0,
		};
	})();

	useEffect(() => {
		if (isOpen) {
			if (editingTable) {
				setName(editingTable.name);
				setSelectedTableIds([...editingTable.quote_table_ids]);
				setSelectedValueColumns([...editingTable.value_column_names]);
				setIncludeColumnTotals(editingTable.include_column_totals ?? false);
			} else {
				setName("");
				setSelectedTableIds([]);
				setSelectedValueColumns([]);
				setIncludeColumnTotals(false);
			}
			setNameError("");
			setValidationErrors([]);
		}
	}, [isOpen, editingTable]);

	const handleTableToggle = useCallback(
		(tableId: number) => {
			setSelectedTableIds((prev) =>
				prev.includes(tableId)
					? prev.filter((id) => id !== tableId)
					: [...prev, tableId]
			);
			setSelectedValueColumns([]);
			setValidationErrors([]);
		},
		[]
	);

	const handleValueColumnToggle = useCallback(
		(columnName: string) => {
			setSelectedValueColumns((prev) =>
				prev.includes(columnName)
					? prev.filter((c) => c !== columnName)
					: [...prev, columnName]
			);
			setValidationErrors([]);
		},
		[]
	);

	const handleSubmit = useCallback(async () => {
		if (!name.trim()) {
			setNameError("Summary table name is required");
			return;
		}
		setNameError("");

		if (selectedTableIds.length === 0) {
			setValidationErrors(["Please select at least one quote table"]);
			return;
		}

		if (selectedValueColumns.length === 0) {
			setValidationErrors(["Please select at least one value column"]);
			return;
		}

		setIsValidating(true);
		try {
			const result = await validateSummaryTableValueColumns(
				rfxId,
				selectedTableIds,
				selectedValueColumns
			);
			if (!result.valid) {
				setValidationErrors(result.errors);
				return;
			}
			onSubmit(name.trim(), selectedTableIds, selectedValueColumns, includeColumnTotals);
			onClose();
		} catch {
			setValidationErrors(["Failed to validate configuration. Please try again."]);
		} finally {
			setIsValidating(false);
		}
	}, [name, selectedTableIds, selectedValueColumns, includeColumnTotals, rfxId, onSubmit, onClose]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent data-testid="create-summary-table-modal">
				<ModalHeader>{isEditing ? "Edit Summary Table" : "Create Summary Table"}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<FormControl isInvalid={!!nameError} className="mb-4">
						<FormLabel className="text-sm font-medium text-gray-700">
							Summary Table Name
						</FormLabel>
						<Input
							value={name}
							onChange={(e) => {
								setName(e.target.value);
								if (nameError) {
									setNameError("");
								}
							}}
							placeholder="e.g. Overall Summary"
							data-testid="summary-table-name-input"
						/>
						{nameError && (
							<FormErrorMessage>{nameError}</FormErrorMessage>
						)}
					</FormControl>

					<FormControl className="mb-4">
						<FormLabel className="text-sm font-medium text-gray-700">
							Quote Tables
						</FormLabel>
						<Text className="mb-2 text-xs text-gray-500">
							Select which quote tables to include as rows
						</Text>
						<Stack spacing={2}>
							{availableQuoteTables.map((qt) => (
								<Checkbox
									key={qt.id}
									isChecked={selectedTableIds.includes(qt.id)}
									onChange={() => handleTableToggle(qt.id)}
									data-testid={`select-table-${qt.id}`}
								>
									<Text className="text-sm">{qt.name}</Text>
								</Checkbox>
							))}
						</Stack>
					</FormControl>

					{selectedTableIds.length >= 2 && hasNoMatches && (
						<Alert
							status="warning"
							className="mb-4 rounded-md"
							data-testid="no-matching-columns-warning"
						>
							<AlertIcon />
							<div>
								<Text className="text-sm font-medium">
									No matching cost columns found
								</Text>
								<Text className="text-xs text-gray-600">
									To create a summary table, the selected quote tables need at least one
									value column with the same name (e.g. &quot;Rate&quot;). Add matching value
									column names to your quote tables first.
								</Text>
							</div>
						</Alert>
					)}

					{matchingValueColumns.length > 0 && (
						<FormControl className="mb-4">
							<FormLabel className="text-sm font-medium text-gray-700">
								Value Columns
							</FormLabel>
							<Text className="mb-2 text-xs text-gray-500">
								Select which value columns to use in the summary
							</Text>
							<Stack spacing={2}>
								{matchingValueColumns.map((col) => (
									<Checkbox
										key={col}
										isChecked={selectedValueColumns.includes(col)}
										onChange={() => handleValueColumnToggle(col)}
										data-testid={`select-value-column-${col}`}
									>
										<Text className="text-sm">{col}</Text>
									</Checkbox>
								))}
							</Stack>
						</FormControl>
					)}

					<FormControl className="mb-4 flex items-center justify-between">
						<FormLabel className="mb-0 text-sm font-medium text-gray-700">
							Include Column Totals
						</FormLabel>
						<Switch
							isChecked={includeColumnTotals}
							onChange={() => setIncludeColumnTotals((prev) => !prev)}
							data-testid="include-column-totals-switch"
						/>
					</FormControl>

					{validationErrors.length > 0 && (
						<Alert
							status="error"
							className="mt-3 rounded-md"
							data-testid="validation-errors"
						>
							<AlertIcon />
							<div>
								{validationErrors.map((err) => (
									<Text key={err} className="text-sm">
										{err}
									</Text>
								))}
							</div>
						</Alert>
					)}
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						onClick={onClose}
						className="mr-3"
						data-testid="cancel-summary-table"
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						onClick={handleSubmit}
						disabled={isValidating}
						data-testid="submit-summary-table"
					>
						{isValidating ? "Validating..." : isEditing ? "Save" : "Create"}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { CreateSummaryTableModal };
